import type { PropType } from 'vue';
import type { Nullable } from '../../types';
import type { TTextInputProps } from './TTextInput.types';
import { Icon } from '@iconify/vue';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent, ref } from 'vue';
import { useTheme } from '../../composables/theme';
import { useStyles } from './TTextInput.styles';

const defaultProps = {
  type: 'text',
};

export const TTextInput = defineComponent({
  name: 'TTextInput',
  emits: ['input', 'change', 'update:modelValue', 'focus', 'blur'],
  props: {
    error: Boolean as PropType<TTextInputProps['error']>,
    disabled: Boolean as PropType<TTextInputProps['disabled']>,
    leftIcon: String as PropType<TTextInputProps['leftIcon']>,
    rightIcon: String as PropType<TTextInputProps['rightIcon']>,
    label: String as PropType<TTextInputProps['label']>,
    placeholder: String as PropType<TTextInputProps['placeholder']>,
    helperText: String as PropType<TTextInputProps['helperText']>,
    value: [Number, String, Date] as PropType<TTextInputProps['value']>,
    modelValue: [Number, String, Date] as PropType<
      TTextInputProps['modelValue']
    >,
    fullWidth: Boolean as PropType<TTextInputProps['fullWidth']>,
    id: String as PropType<TTextInputProps['id']>,
    type: String as PropType<TTextInputProps['type']>,
    maxlength: [String, Number] as PropType<TTextInputProps['maxlength']>,
    min: [String, Number] as PropType<TTextInputProps['min']>,
    max: [String, Number] as PropType<TTextInputProps['max']>,
    step: [String, Number] as PropType<TTextInputProps['step']>,
  },
  setup(_props, { attrs, emit, slots, expose }) {
    const props = computed(() => merge({}, defaultProps, _props, attrs));
    const baseClass = useStyles();
    const theme = useTheme();

    const internalValue = computed({
      get: () => (props.value.modelValue !== undefined
        ? props.value.modelValue
        : props.value.value),
      set: (newValue) => {
        emit('update:modelValue', newValue);
        emit('input', newValue);
        emit('change', newValue);
      },
    });

    const handleInput = (event: Event) => {
      internalValue.value = (event.target as HTMLInputElement).value;
    };

    const handleFocus = (event: Event) => {
      emit('focus', event);
    };

    const handleBlur = (event: Event) => {
      emit('blur', event);
    };

    const input = ref<Nullable<HTMLInputElement>>(null);
    const focus = () => {
      input.value?.focus();
    };
    const blur = () => {
      input.value?.blur();
    };
    expose({ focus, blur });

    return () => (
      <div
        class={[
          baseClass.value,
          `${baseClass.value}__container`,
          {
            [`${baseClass.value}--error`]: props.value.error,
            [`${baseClass.value}--full-width`]: props.value.fullWidth,
            [`${baseClass.value}--multiline`]: props.value.multiline,
          },
        ]}
      >
        {slots.label?.()
        || (props.value.label && (
          <label class={[`${baseClass.value}__label`]} for={props.value.id}>
            {props.value.label}
          </label>
        ))}
        <div class={`${baseClass.value}__input-wrapper`}>
          {slots.leftIcon?.()
          || (props.value.leftIcon && (
            <Icon
              icon={`${theme.value.iconSet}:${props.value.leftIcon}`}
              class={[
                `${baseClass.value}__icon`,
                `${baseClass.value}__icon--left`,
              ]}
            />
          ))}
          <input
            {...attrs}
            id={props.value.id}
            type={props.value.multiline ? undefined : props.value.type}
            class={[
              `${baseClass.value}__input`,
              {
                [`${baseClass.value}__input--left-icon`]: slots.leftIcon?.() || props.value.leftIcon,
                [`${baseClass.value}__input--right-icon`]: slots.rightIcon?.() || props.value.rightIcon,
              },
            ]}
            placeholder={props.value.placeholder}
            value={internalValue.value}
            onInput={handleInput}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={props.value.disabled}
            maxlength={props.value.maxlength}
            min={props.value.min}
            max={props.value.max}
            ref={input}
            step={props.value.step}
          />
          {slots.rightIcon?.()
          || (props.value.rightIcon && (
            <Icon
              icon={`${theme.value.iconSet}:${props.value.rightIcon}`}
              class={[
                `${baseClass.value}__icon`,
                `${baseClass.value}__icon--right`,
              ]}
            />
          ))}
        </div>
        {slots.helperText?.()
        || (props.value.helperText && (
          <p class={`${baseClass.value}__helper-text`}>
            {props.value.helperText}
          </p>
        ))}
      </div>
    );
  },
});
